/**
 * @fileoverview Utility functions for managing Google Tag Manager data layer and search results.
 */

import { hasNonEmptyArray } from "../Utils";

/**
 * Pushes search event data to the Google Tag Manager data layer.
 * @param {Object} searchEventData - The search event data to be pushed to the data layer.
 */
export const searchDayaLayerPush = (searchEventData) => {
    window.dataLayer.push(searchEventData);
}

/**
 * Sends search data to Google Tag Manager with a delay to avoid spamming the data layer.
 * @param {number} dataLayerTimeout - A timeout identifier for managing delays.
 * @param {Object} hits - The hits object from Algolia's InstantSearch.
 * @param {string} eventName - The name of the event to be sent to the data layer.
 * @returns {number} The new timeout identifier.
 */
export const gtmSendSearchData = (dataLayerTimeout, hits, eventName) => {
    if (dataLayerTimeout) {
        clearTimeout(dataLayerTimeout);
    }

    dataLayerTimeout = setTimeout(() => {
        const { instantSearchInstance } = hits;
        const currentQuery = instantSearchInstance.helper?.state.query;
        const filters = instantSearchInstance.helper?.state.disjunctiveFacetsRefinements;

        if (!currentQuery && !hasNonEmptyArray(filters)) {
            return false;
        }

        const searchEventData = {
            event: eventName,
            hitCount: hits.results?.nbHits,
            query: currentQuery,
            filters: filters
        };

        searchDayaLayerPush(searchEventData);
    }, 1000);

    return dataLayerTimeout;
}

/**
 * Updates the counter for search results.
 * @param {Object} hits - The hits object from Algolia's InstantSearch.
 */
export const updateResultCounter = (hits) => {
    const resultCounterSelector = '#resultCounter';

    if (hits?.results?.nbHits === 0) {
        document.querySelector(resultCounterSelector).textContent = 'No results found';
        return;
    }

    if (hits.results && !isNaN(hits.results.page) && !isNaN(hits.results.nbPages) && !isNaN(hits.results.nbHits)) {
        const start = (hits.results.page * hits.results.hitsPerPage) + 1;
        const end = Math.min(((hits.results.page + 1) * hits.results.hitsPerPage), hits.results.nbHits);
        document.querySelector(resultCounterSelector).textContent = `Showing ${start}-${end} of ${hits.results.nbHits} results`;
    } else {
        document.querySelector(resultCounterSelector).textContent = `Loading results...`;
    }
}

/**
 * Sets the most relevant results counter.
 * @param {number} total - The total number of results.
 */
export const setMostRelevantResultsCounter = (total) => {
    const resultCounterSelector = '#resultCounter';

    if (total === 0) {
        document.querySelector(resultCounterSelector).textContent = 'No results found';
        return;
    }

    if (total) {
        document.querySelector(resultCounterSelector).textContent = `Showing 1-${total} of ${total} results`;
    } else {
        document.querySelector(resultCounterSelector).textContent = `Loading results...`;
    }
}

/**
 * Sets the initial counter for filters applied in the search.
 * @param {Object} filters - The filters object from Algolia's InstantSearch state.
 * @param {string} key - The key representing the specific filter category.
 */
export const setInitialFiltersCounter = (filters, key) => {
    const counterElement = document.querySelector(`.${key}-filters-counter-js`);
    const items = filters ? filters[key] : false;

    if (! items || (items && ! items.length)) {
        counterElement.style.display = 'none';
        return;
    }

    const length = filters[key].length;
    counterElement.style.display = 'flex';
    counterElement.textContent = length;
}

/**
 * Generates HTML for no results message.
 * @returns {string} The HTML string for the no results message.
 */
export const getNoResultsHtml = () => {
    return `<p id="no-results">There are no items to display for the selected content type. <br>Please try selecting a different content type.</p>`;
}

/**
 * Updates the label of the mobile dropdown.
 * @param {HTMLElement} mobileTab - The mobile tab element.
 */
export const updateMobileDropdownLabel = (mobileTab) => {
    const dropdownLabel = document.querySelector('.dropdown-label-js');
    dropdownLabel.textContent = mobileTab.textContent;
}

/**
 * Handles URL updates by beautifying the URL and updating the browser history.
 * @param {Event} e - The event object.
 */
export const urlUpdateHandler = (e) => {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    url.search = beautifyUrl(params.toString());
    history.pushState({}, '', url.toString());
}

/**
 * Beautifies a URL by replacing spaces with plus signs and encoding certain characters.
 * @param {string} url - The URL string to beautify.
 * @returns {string} The beautified URL.
 */
const beautifyUrl = (url) => {
    return url.replace(/ /g, '+').replace(/%2C/g, ',');
}
